import { drawerById, fileCard, priceFor, type Card, type Finish } from '@mtg/shared';
import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { CardImage } from './CardImage.tsx';
import { FinishBadge } from './FinishBadge.tsx';

export type Item = { key: string; card: Card; finish: Finish; quantity: number; lang?: string; condition?: string };

type Section = { title: string; items: Item[] };

const count = (items: Item[]) => items.reduce((n, i) => n + i.quantity, 0);

/**
 * A drawer's cards by divider, front to back, then anything that belongs
 * elsewhere or has no spot. Without a drawer (the intake pile): cards
 * waiting to be filed. Decks use DeckView.
 */
export function CardGroups({
  items,
  drawerId,
  actions,
}: {
  items: Item[];
  /** Which drawer this is (none for the intake pile). */
  drawerId?: string;
  actions?: (item: Item) => ReactNode;
}) {
  const drawer = drawerId ? drawerById(drawerId) : undefined;
  const spots = new Map(items.map((i) => [i.key, fileCard(i.card)]));
  const here = (i: Item) => {
    const spot = spots.get(i.key)!;
    return spot.drawerId === drawer?.id && (drawer?.dividers as readonly string[]).includes(spot.divider);
  };
  const sections: Section[] = [
    ...(drawer?.dividers ?? []).map((divider) => ({
      title: divider,
      items: items.filter((i) => here(i) && (spots.get(i.key) as { divider: string }).divider === divider),
    })),
    { title: drawer ? 'Belongs in another drawer' : 'Not filed yet', items: items.filter((i) => !here(i) && spots.get(i.key)!.drawerId) },
    { title: 'No filing spot yet', items: items.filter((i) => !spots.get(i.key)!.drawerId) },
  ];

  const grid = (list: Item[]) => (
    <ul className="card-grid">
      {list.map((item) => (
        <li key={item.key} className="tile">
          <Link href={`/cards/${item.card.scryfallId}`} className="tile-img">
            <CardImage card={item.card} />
          </Link>
          {item.quantity > 1 && <span className="qty">×{item.quantity}</span>}
          <div className="tile-meta">
            <strong>{item.card.name}</strong>
            <span className="muted small">
              {item.card.setCode.toUpperCase()} {item.card.collectorNumber}
              <FinishBadge card={item.card} finish={item.finish} />
              {item.lang && item.lang !== 'en' && <span className="badge">{item.lang.toUpperCase()}</span>}
              {item.condition && item.condition !== 'NM' && <span className="badge">{item.condition}</span>}
            </span>
            <span className="muted small">{priceLabel(item)}</span>
          </div>
          {actions?.(item)}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      {sections
        .filter((s) => s.items.length > 0)
        .map((s) => (
          <section key={s.title} className="group">
            <h2>
              {s.title} <span className="muted">({count(s.items)})</span>
            </h2>
            {grid(s.items)}
          </section>
        ))}
    </>
  );
}

function priceLabel(item: Item) {
  const each = priceFor(item.card, item.finish);
  if (each === null) return 'No price';
  const fmt = (n: number) => `$${n.toFixed(2)}`;
  return item.quantity > 1 ? `${fmt(each)} each · ${fmt(each * item.quantity)}` : fmt(each);
}
